/**
 * Trip CO₂ Calculator — shared pill primitives.
 *
 * Both `PerspectiveSection` and `TripSummaryCard` render the user's
 * trip as one (passenger modes) or two (vehicle modes) rounded pills:
 *
 *   passenger modes:  [ ✈️ Plane · Berlin ↔ Paris · Round trip ]
 *   vehicle modes:    [ Berlin ↔ Paris · Round trip ]
 *                     [ 🚗 Car · 2 passengers · Petrol ]
 *
 * The visual identity and the vehicle-pill text builder are shared
 * here so the two consumers never drift in pill style, copy, or
 * decision logic about when to render one vs. two pills.
 *
 * Why a module instead of a component: the pill JSX itself is small
 * and lives inside each consumer (PerspectiveSection wraps it in a
 * centred row, TripSummaryCard pairs it with an Edit button on the
 * right) — those are different containers. The shared bits are the
 * styling tokens and the vehicle-config formatter.
 */

import type { CSSProperties } from 'react';
import { MODE_DISPLAY, type ConfigByMode, type TransportMode } from './modes';

export const pillStyle: CSSProperties = {
  display: 'inline-flex',
  alignItems: 'center',
  gap: 6,
  padding: '4px 10px',
  background: '#fff',
  border: '1px solid #e2e8f0',
  borderRadius: 999,
  fontSize: 12,
  fontWeight: 600,
  color: '#0f172a',
};

export const dotStyle: CSSProperties = { color: '#94a3b8' };

/**
 * Pre-formatted segments for the second pill, used only for vehicle
 * modes (car / van / motorcycle) where there are non-trivial config
 * details worth surfacing alongside the route pill. Returns `null`
 * for passenger modes — the caller uses that to fall back to the
 * single-pill layout.
 *
 * `fuel` is included for all three vehicle modes even though van and
 * motorcycle have it locked in the UI: making the assumption explicit
 * in the pill helps recipients understand the comparison without
 * having to dig into the methodology panel.
 */
export function formatVehiclePill(
  mode: TransportMode,
  config: ConfigByMode[TransportMode]
): { emoji: string; label: string; occupancy: string; fuel: string } | null {
  if (mode === 'car') {
    const c = config as ConfigByMode['car'];
    const word = c.occupancy === 1 ? 'passenger' : 'passengers';
    return {
      emoji: MODE_DISPLAY.car.emoji,
      label: MODE_DISPLAY.car.label,
      occupancy: `${c.occupancy} ${word}`,
      fuel: c.fuel,
    };
  }
  if (mode === 'van') {
    const c = config as ConfigByMode['van'];
    const word = c.occupancy === 1 ? 'passenger' : 'passengers';
    return {
      emoji: MODE_DISPLAY.van.emoji,
      label: MODE_DISPLAY.van.label,
      occupancy: `${c.occupancy} ${word}`,
      fuel: c.fuel,
    };
  }
  if (mode === 'motorcycle') {
    const c = config as ConfigByMode['motorcycle'];
    return {
      emoji: MODE_DISPLAY.motorcycle.emoji,
      label: MODE_DISPLAY.motorcycle.label,
      occupancy: c.occupancy === 1 ? '1 rider' : '2 riders',
      fuel: c.fuel,
    };
  }
  return null;
}
